import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../hooks/useAuth";
import SupabaseAuthForm from "../../components/SupabaseAuthForm";
import { supabase } from "../../supabase";
import { Sparkles } from "lucide-react";
import GuestPortalHeader from "./components/GuestPortalHeader";
import GuestPortalNav from "./components/GuestPortalNav";
import GuestPortalContent from "./components/GuestPortalContent";
import GuestPortalExtras from "./components/GuestPortalExtras";
import InvoiceCorrectionModal from "./components/InvoiceCorrectionModal";

/**
 * GuestPortalLayout component provides the primary workspace/dashboard and routing logic for B2C hotel guests.
 * It manages guest authentication, self-healing tenant ID resolution from URL paths, and exposes
 * tabs for guest loyalty logs, stay histories, and invoice document downloads/corrections.
 *
 * @param {Object} props - The component props.
 * @param {Object} props.user - The authenticated guest's user profile.
 * @returns {React.ReactElement} The rendered GuestPortalLayout layout.
 */
export default function GuestPortalLayout({ user: currentUser }: { user: { tenantId?: string; [key: string]: any } | null }) {
  const { tenantId: routeTenantId } = useParams();
  const navigate = useNavigate();
  const { userId, getToken, user: authUser, signOut } = useAuth();
  const { t, i18n } = useTranslation();

  // Self-healing tenant ID resolution from URL path segments or custom claims
  const cleanRouteTenantId = (routeTenantId && routeTenantId !== "undefined") ? routeTenantId : null;
  const pathParts = window.location.pathname.split("/");
  const fallbackPathTenantId = (pathParts[1] === "portal" && pathParts[2] && pathParts[2] !== "undefined") ? pathParts[2] : null;
  const tenantId = cleanRouteTenantId || currentUser?.tenantId || fallbackPathTenantId || undefined;

  // Persist guest tenant context for redirect-safe Supabase auth token syncing
  useEffect(() => {
    if (tenantId && tenantId !== "undefined") {
      localStorage.setItem("adapthub_guest_tenant", tenantId);
      localStorage.setItem("adapthub_is_guest", "true");
    }
  }, [tenantId]);

  // Self-healing tenant ID URL redirect
  useEffect(() => {
    if (userId && tenantId && tenantId !== "undefined") {
      const currentPath = window.location.pathname;
      if (currentPath.includes("/portal/undefined/")) {
        const newPath = currentPath.replace("/portal/undefined/", `/portal/${tenantId}/`);
        navigate(newPath, { replace: true });
      } else if (currentPath === `/portal/undefined` || currentPath === `/portal/undefined/`) {
        navigate(`/portal/${tenantId}/dashboard`, { replace: true });
      } else if (!routeTenantId || routeTenantId === "undefined") {
        navigate(`/portal/${tenantId}/dashboard`, { replace: true });
      }
    }
  }, [userId, tenantId, routeTenantId, navigate]);

  const [activeTab, setActiveTab] = useState("dashboard"); // dashboard, reservations, invoices
  const [authMode, setAuthMode] = useState("login"); // login, register

  const [reservations, setReservations] = useState<any[]>([]);
  const [invoices, setInvoices] = useState<any[]>([]);
  const [transactions, setTransactions] = useState<any[]>([]);
  // 2026-10-16: server-truth loyalty balance from the reservations API.
  // The users-profile (currentUser) never carries loyaltyPoints — that value
  // lives on the guests row and is recalculated server-side, so it must be
  // fetched, not read from the profile object (was the source of "0 pts").
  const [loyaltyPoints, setLoyaltyPoints] = useState<number>(0);
  // Server-truth guest profile for the My Data tab (read-only display;
  // changes go through the staff-mediated data-change request flow).
  const [guestProfile, setGuestProfile] = useState<any>(null);

  const [isLoadingReservations, setIsLoadingReservations] = useState(false);
  const [isLoadingInvoices, setIsLoadingInvoices] = useState(false);
  const [isSubmittingCorrection, setIsSubmittingCorrection] = useState(false);



  // Search & Filter state for stay history view
  const [searchTerm, setSearchTerm] = useState("");
  // Debounce the search term so the filter below doesn't re-run on every keystroke.
  const [debouncedSearch, setDebouncedSearch] = useState("");
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm), 250);
    return () => clearTimeout(timer);
  }, [searchTerm]);
  const [statusFilter, setStatusFilter] = useState("All");
  const [currentPage, setCurrentPage] = useState(1);

  // Reset page when search or status filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter]);

  // Invoice Correction Modal State
  const [selectedInvoice, setSelectedInvoice] = useState<any>(null);
  const [correctionForm, setCorrectionForm] = useState({
    originalCompanyName: "",
    correctedCompanyName: "",
    street: "",
    zipCode: "",
    city: "",
    vatNumber: "",
    notes: "",
    languageCode: "de"
  });
  const [correctionSuccess, setCorrectionSuccess] = useState(false);
  const [correctionError, setCorrectionError] = useState("");

  // 1. Fetch Reservations
  const fetchReservations = useCallback(async () => {
    if (!userId) return;
    setIsLoadingReservations(true);
    try {
      const token = await getToken();
      const res = await fetch("/api/guest/reservations", {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setReservations(data.reservations || []);
        setLoyaltyPoints(typeof data.loyaltyPoints === "number" ? data.loyaltyPoints : 0);
        if (data.profile) setGuestProfile(data.profile);
      }
    } catch (err) {
      console.error("Error fetching guest reservations:", err);
    } finally {
      setIsLoadingReservations(false);
    }
  }, [userId, getToken]);

  // 2. Fetch Invoices
  const fetchInvoices = useCallback(async () => {
    if (!userId) return;
    setIsLoadingInvoices(true);
    try {
      const token = await getToken();
      const res = await fetch("/api/guest/invoices", {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setInvoices(data.invoices || []);
      }
    } catch (err) {
      console.error("Error fetching guest invoices:", err);
    } finally {
      setIsLoadingInvoices(false);
    }
  }, [userId, getToken]);

  // 3. Sync transactions in real-time from Supabase
  useEffect(() => {
    if (!userId || !tenantId) return;

    const fetchTransactions = async () => {
      const { data: rows } = await supabase
        .from('loyalty_transactions')
        .select('*')
        .eq('tenant_id', tenantId)
        .eq('guest_id', userId)
        .order('created_at', { ascending: false })
        // Bound the query per AGENTS.md §1 — a guest's own loyalty history.
        // 50 newest entries are far more than any guest-portal view renders.
        .limit(50);
      const list = (rows || []).map(row => ({
        id: row.id,
        ...row,
        ...(row.data || {}),
        createdAt: row.created_at ? new Date(row.created_at).toLocaleDateString() : ""
      }));
      setTransactions(list);
    };
    fetchTransactions();

    // Realtime handled by centralized realtimeStore
  }, [userId, tenantId]);

  // Trigger loads on tab change
  useEffect(() => {
    if (userId) {
      if (activeTab === "reservations") {
        fetchReservations();
      } else if (activeTab === "invoices") {
        fetchInvoices();
      } else if (activeTab === "dashboard") {
        fetchReservations();
      }
    }
  }, [activeTab, userId, fetchReservations, fetchInvoices]);

  // Load reservations on mount for dashboard overview
  useEffect(() => {
    if (userId) {
      fetchReservations();
    }
  }, [userId, fetchReservations]);

  // 4. Download PDF
  const downloadPdf = async (invoiceId: string, invoiceNumber: string) => {
    try {
      const token = await getToken();
      const res = await fetch(`/api/guest/invoices?pdf=true&invoiceId=${invoiceId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error(t('guestPortal.downloadError'));

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${t("guestPortal.invoicePrefix")}_${invoiceNumber || invoiceId}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Error downloading invoice PDF:", err);
      alert(t("guestPortal.downloadError"));
    }
  };

  // 5. Submit Invoice Correction Request
  const handleCorrectionSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedInvoice) return;

    setIsSubmittingCorrection(true);
    setCorrectionError("");
    setCorrectionSuccess(false);

    try {
      const token = await getToken();
      const res = await fetch("/api/guest/invoice-correction", {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          invoiceId: selectedInvoice.id,
          originalCompanyName: correctionForm.originalCompanyName,
          correctedCompanyName: correctionForm.correctedCompanyName,
          street: correctionForm.street,
          zipCode: correctionForm.zipCode,
          city: correctionForm.city,
          vatNumber: correctionForm.vatNumber,
          notes: correctionForm.notes,
          languageCode: correctionForm.languageCode
        })
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || t('common.error'));
      }

      setCorrectionSuccess(true);
      setCorrectionForm({
        originalCompanyName: "",
        correctedCompanyName: "",
        street: "",
        zipCode: "",
        city: "",
        vatNumber: "",
        notes: "",
        languageCode: "de"
      });
      setTimeout(() => {
        setSelectedInvoice(null);
        setCorrectionSuccess(false);
      }, 3000);
    } catch (err) {
      setCorrectionError((err instanceof Error ? err.message : String(err)) || t("guestPortal.correctionSubmitError"));
    } finally {
      setIsSubmittingCorrection(false);
    }
  };

  const upcomingReservations = reservations.filter(
    (r: { status?: string }) => r.status === "Confirmed" || r.status === "InHouse"
  );

  // --- LOGOUT HANDLER ---
  const handleLogout = async () => {
    await signOut();
    localStorage.removeItem("adapthub_login_method");
    localStorage.removeItem("adapthub_is_guest");
    localStorage.removeItem("adapthub_guest_tenant");
    navigate(`/portal/${tenantId}/login`);
  };

  // --- LOGGED OUT STATE RENDERING ---
  if (!userId) {
    return (
      <div className="min-h-screen bg-bg flex flex-col items-center justify-center p-4 md:p-6 font-sans relative overflow-hidden text-fg">
        {/* Glowing UI backgrounds */}
        <div className="absolute top-1/4 left-1/4 w-72 h-72 bg-accent/10 rounded-full blur-[100px] animate-pulse duration-[10s]" />
        <div className="absolute bottom-1/4 right-1/4 w-72 h-72 bg-indigo-500/10 rounded-full blur-[100px] animate-pulse duration-[8s]" />

        <div className="relative w-full max-w-md text-center mb-8 animate-in zoom-in">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-surface border border-border rounded-3xl shadow-xl text-accent mb-4">
            <Sparkles size={32} />
          </div>
          <h1 className="text-3xl md:text-4xl font-black display-font tracking-tight mb-2 uppercase">
            {t("guestPortal.title")}
          </h1>
          <p className="text-[10px] font-black text-muted tracking-[0.2em] uppercase">
            {t("guestPortal.subtitle")}
          </p>
        </div>

        <div className="relative w-full max-w-md bg-surface border border-border rounded-[2.5rem] shadow-2xl p-6 md:p-8 animate-in fade-in zoom-in">
          <div className="flex gap-1 p-1 bg-bg border border-border rounded-xl mb-6 shadow-inner">
            <button
              onClick={() => setAuthMode("login")}
              className={`flex-1 py-2 rounded-lg text-[10px] font-black uppercase tracking-wider transition-all ${authMode === "login" ? "bg-accent text-white shadow" : "text-muted hover:bg-white/50"
                }`}
            >
              {t("guestPortal.logIn")}
            </button>
            <button
              onClick={() => setAuthMode("register")}
              className={`flex-1 py-2 rounded-lg text-[10px] font-black uppercase tracking-wider transition-all ${authMode === "register" ? "bg-accent text-white shadow" : "text-muted hover:bg-white/50"
                }`}
            >
              {t("guestPortal.register")}
            </button>
          </div>

          <div className="flex justify-center">
            <SupabaseAuthForm
              mode={authMode === "login" ? "sign_in" : "sign_up"}
              tenantId={tenantId}
              redirectTo={undefined}
            />
          </div>
        </div>
      </div>
    );
  }

  // --- LOGGED IN STATE RENDERING ---
  return (
    <div className="min-h-screen bg-bg flex flex-col font-sans text-fg">
      {/* Top Banner Navigation */}
      <GuestPortalHeader
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        loyaltyPoints={loyaltyPoints}
        handleLogout={handleLogout}
        t={t}
      />

      {/* Mobile Tab Navigation */}
      <GuestPortalNav
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        t={t}
      />

      {/* Extended Tabs: My Data / Requests / Rewards */}
      {(activeTab === "profile" || activeTab === "requests" || activeTab === "rewards") && (
        <GuestPortalExtras
          activeTab={activeTab as "profile" | "requests" | "rewards"}
          profile={guestProfile}
          loyaltyPoints={loyaltyPoints}
          reservations={reservations}
          onPointsChange={setLoyaltyPoints}
          getToken={getToken}
          t={t}
          language={i18n.language}
        />
      )}

      {/* Main View Area */}
      <GuestPortalContent
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        loyaltyPoints={loyaltyPoints}
        reservations={reservations}
        invoices={invoices}
        transactions={transactions}
        isLoadingReservations={isLoadingReservations}
        isLoadingInvoices={isLoadingInvoices}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        statusFilter={statusFilter}
        setStatusFilter={setStatusFilter}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        debouncedSearch={debouncedSearch}
        downloadPdf={downloadPdf}
        setSelectedInvoice={setSelectedInvoice}
        setCorrectionForm={setCorrectionForm}
        fetchReservations={fetchReservations}
        fetchInvoices={fetchInvoices}
        currentUser={currentUser}
        authUser={authUser}
        upcomingReservations={upcomingReservations}
        t={t}
      />

      {/* --- MODAL: INVOICE CORRECTION FORM --- */}
      {selectedInvoice && (
        <InvoiceCorrectionModal
          selectedInvoice={selectedInvoice}
          setSelectedInvoice={setSelectedInvoice}
          correctionForm={correctionForm}
          setCorrectionForm={setCorrectionForm}
          handleCorrectionSubmit={handleCorrectionSubmit}
          correctionSuccess={correctionSuccess}
          correctionError={correctionError}
          isSubmittingCorrection={isSubmittingCorrection}
          t={t}
        />
      )}
    </div>
  );
}

